import { useState } from 'react';
import type { VendorInfo, EntityMatch, Address, MatchedVendorDetails } from '@acitra/shared';
import { VendorMatchingModal } from './VendorMatchingModal';

interface VendorCardProps {
  tenantId: string;
  invoiceId: string;
  extractedVendor?: VendorInfo;
  originalVendor?: VendorInfo;
  effectiveCurrency?: string;
  vendorMatch?: EntityMatch;
  matchedVendorDetails?: MatchedVendorDetails;
  onMatchUpdated: () => void;
  onEdit?: () => void;
}

export function VendorCard({
  tenantId,
  invoiceId,
  extractedVendor,
  originalVendor,
  effectiveCurrency,
  vendorMatch,
  matchedVendorDetails,
  onMatchUpdated,
  onEdit,
}: VendorCardProps) {
  const [showModal, setShowModal] = useState(false);

  const isMatched = !!vendorMatch;
  const hasEdits = originalVendor !== undefined && extractedVendor !== originalVendor;

  // Field-level change detection
  const nameChanged = hasEdits && (extractedVendor?.name || '') !== (originalVendor?.name || '');
  const effectiveAddr = extractedVendor?.address;
  const originalAddr = originalVendor?.address;
  const addressChanged =
    hasEdits &&
    ((effectiveAddr?.line1 || '') !== (originalAddr?.line1 || '') ||
      (effectiveAddr?.line2 || '') !== (originalAddr?.line2 || '') ||
      (effectiveAddr?.city || '') !== (originalAddr?.city || '') ||
      (effectiveAddr?.state || '') !== (originalAddr?.state || '') ||
      (effectiveAddr?.postalCode || '') !== (originalAddr?.postalCode || '') ||
      (effectiveAddr?.country || '') !== (originalAddr?.country || ''));
  const emailChanged = hasEdits && (extractedVendor?.email || '') !== (originalVendor?.email || '');
  const phoneChanged = hasEdits && (extractedVendor?.phone || '') !== (originalVendor?.phone || '');
  const accountNumberChanged =
    hasEdits && (extractedVendor?.accountNumber || '') !== (originalVendor?.accountNumber || '');

  const formatAddress = (address: Address | undefined) => {
    if (!address) return null;
    const parts = [
      address.line1,
      address.line2,
      [address.city, address.state, address.postalCode].filter(Boolean).join(', '),
      address.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts : null;
  };

  const vendorAddress = formatAddress(extractedVendor?.address);

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleMatchUpdated = () => {
    setShowModal(false);
    onMatchUpdated();
  };

  return (
    <>
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Vendor</h2>

        <div className="grid grid-cols-2 gap-4">
          {/* Left side: Extracted vendor info */}
          <div className="border border-gray-200 rounded-lg p-4 relative group">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Extracted</p>
              {onEdit && extractedVendor && (
                <button
                  onClick={() => onEdit()}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  Edit
                </button>
              )}
            </div>
            {extractedVendor ? (
              <div className="space-y-1">
                <p className={`font-medium ${nameChanged ? 'text-edited-600' : 'text-gray-900'}`}>
                  {extractedVendor.name}
                </p>
                {vendorAddress && (
                  <div
                    className={`text-sm ${addressChanged ? 'text-edited-600' : 'text-gray-600'}`}
                  >
                    {vendorAddress.map((line, idx) => (
                      <p key={idx}>{line}</p>
                    ))}
                  </div>
                )}
                {extractedVendor.email && (
                  <p className={`text-sm ${emailChanged ? 'text-edited-600' : 'text-gray-600'}`}>
                    {extractedVendor.email}
                  </p>
                )}
                {extractedVendor.phone && (
                  <p className={`text-sm ${phoneChanged ? 'text-edited-600' : 'text-gray-600'}`}>
                    {extractedVendor.phone}
                  </p>
                )}
                {extractedVendor.accountNumber && (
                  <p
                    className={`text-sm ${accountNumberChanged ? 'text-edited-600' : 'text-gray-500'}`}
                  >
                    Acct: {extractedVendor.accountNumber}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No vendor information</p>
            )}
          </div>

          {/* Right side: QuickBooks match or Match button */}
          <div className="border border-gray-200 rounded-lg p-4 relative group">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider">QuickBooks</p>
              {isMatched && (
                <button
                  onClick={() => setShowModal(true)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  Edit
                </button>
              )}
            </div>

            {isMatched && vendorMatch ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900">
                    {matchedVendorDetails?.displayName || vendorMatch.entityName}
                  </p>
                  {vendorMatch.matchMethod === 'rule' ? (
                    <span
                      title="Matched automatically via saved rule"
                      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary-500 flex-shrink-0"
                    >
                      <svg
                        className="w-2.5 h-2.5 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" />
                      </svg>
                    </span>
                  ) : (
                    <span
                      title="Matched manually"
                      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary-500 flex-shrink-0"
                    >
                      <svg
                        className="w-2.5 h-2.5 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  )}
                </div>
                {matchedVendorDetails && (
                  <>
                    {formatAddress(matchedVendorDetails.address) && (
                      <div className="text-sm text-gray-600">
                        {formatAddress(matchedVendorDetails.address)!.map((line, idx) => (
                          <p key={idx}>{line}</p>
                        ))}
                      </div>
                    )}
                    {matchedVendorDetails.email && (
                      <p className="text-sm text-gray-600">{matchedVendorDetails.email}</p>
                    )}
                    {matchedVendorDetails.phone && (
                      <p className="text-sm text-gray-600">{matchedVendorDetails.phone}</p>
                    )}
                    {matchedVendorDetails.accountNumber && (
                      <p className="text-sm text-gray-500">
                        Acct: {matchedVendorDetails.accountNumber}
                      </p>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[80px]">
                <p className="text-sm text-gray-500 mb-3">Not matched</p>
                <button
                  onClick={() => setShowModal(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Match to QuickBooks
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Matching Modal */}
      <VendorMatchingModal
        isOpen={showModal}
        onClose={handleModalClose}
        tenantId={tenantId}
        invoiceId={invoiceId}
        extractedVendor={extractedVendor}
        effectiveCurrency={effectiveCurrency}
        currentMatch={vendorMatch}
        onMatchUpdated={handleMatchUpdated}
      />
    </>
  );
}
